// checkin.jsx — mirrors checkin.html exactly
// Uses jsQR for QR scanning (same lib as HTML version)
// Dark theme UI matching the original
import { useState, useEffect, useRef, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";

const TABS = ["scanner", "walkin", "history"];

export default function CheckIn() {
  const [sp]         = useSearchParams();
  const bookingID    = sp.get("id") || "";
  const [authed,   setAuthed]   = useState(!!localStorage.getItem("authToken"));
  const [email,    setEmail]    = useState(localStorage.getItem("authEmail") || "");
  const [tab,      setTab]      = useState("scanner");
  const [stats,    setStats]    = useState({ checked:0, total:0, walkins:0, rate:"0%" });
  const [scanning, setScanning] = useState(false);
  const [flash,    setFlash]    = useState(null); // {type,icon,name,sub}
  const [history,  setHistory]  = useState([]);
  const [manualQ,  setManualQ]  = useState("");
  const [wiForm,   setWiForm]   = useState({ name:"", email:"", usn:"" });
  const [wiStatus, setWiStatus] = useState(null);
  const [bid,      setBid]      = useState(bookingID || "");
  const [promptBid, setPromptBid] = useState(!bookingID);
  const [bidInput, setBidInput] = useState("");
  // camera lifecycle: 'idle' | 'starting' | 'on' | 'error'
  const [camState, setCamState] = useState("idle");
  const [camError, setCamError] = useState("");

  const videoRef   = useRef(null);
  const canvasRef  = useRef(null);
  const timerRef   = useRef(null);
  const rafRef     = useRef(null);
  const scanLock   = useRef(false);

  // Load jsQR from jsDelivr — cdnjs is NOT in the site CSP script-src, so the
  // old cdnjs URL was silently blocked and window.jsQR never loaded (scanner
  // couldn't decode). jsDelivr is whitelisted in public/_headers.
  const [qrReady, setQrReady] = useState(!!window.jsQR);
  useEffect(() => {
    if (window.jsQR) { setQrReady(true); return; }
    const existing = document.getElementById("jsqr-lib");
    if (existing) { existing.addEventListener("load", () => setQrReady(true)); return; }
    const s = document.createElement("script");
    s.id = "jsqr-lib";
    s.src = "https://cdn.jsdelivr.net/npm/jsqr@1.4.0/dist/jsQR.min.js";
    s.onload  = () => setQrReady(true);
    s.onerror = () => toast.error("Couldn't load the QR scanner library. Use manual search below.");
    document.head.appendChild(s);
  }, []);

  const loadStats = useCallback(async () => {
    if (!bid) return;
    const token = localStorage.getItem("authToken") || "";
    const d = await api.attendance(bid).catch(() => null);
    if (d && d.success) {
      setStats({
        checked: d.totalCheckins    || 0,
        total:   d.totalRegistered  || 0,
        walkins: d.walkins          || 0,
        rate:    d.attendanceRate   || "0%",
      });
    }
  }, [bid]);

  useEffect(() => { if (bid) loadStats(); }, [bid, loadStats]);

  const flashResult = (type, icon, name, sub) => {
    setFlash({ type, icon, name, sub });
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => { setFlash(null); scanLock.current = false; }, type === "ok" ? 2500 : 4000);
  };

  const dismissFlash = () => { setFlash(null); scanLock.current = false; };

  const processQr = async (regID, qrToken) => {
    if (scanLock.current) return;
    scanLock.current = true;
    const d = await api.checkin(bid, regID, "Main", 1).catch(() => null);
    if (!d) { flashResult("fail", "✗", "Error", "Network error."); return; }
    if (d.success && d.checkedIn) {
      flashResult("ok", "✓", d.name, "Gate: Main");
      setHistory(h => [{ name: d.name, type: "qr", time: new Date() }, ...h].slice(0, 30));
      loadStats();
    } else if (d.success && d.matches) {
      flashResult("warn", "⚠", d.matches[0].name, d.matches[0].alreadyCheckedIn ? "Already checked in" : "Found");
      if (!d.matches[0].alreadyCheckedIn) setTimeout(() => { scanLock.current = false; }, 3000);
    } else {
      flashResult("fail", "✗", "Not found", d.message || "Check-in failed.");
    }
  };

  const scanFrame = useCallback(() => {
    const video  = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas || !window.jsQR) { rafRef.current = requestAnimationFrame(scanFrame); return; }
    if (video.readyState === video.HAVE_ENOUGH_DATA && !scanLock.current) {
      canvas.width  = video.videoWidth;
      canvas.height = video.videoHeight;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
      const img = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const qr  = window.jsQR(img.data, img.width, img.height, { inversionAttempts: "dontInvert" });
      if (qr && qr.data && qr.data.includes("reg=")) {
        try {
          const url   = new URL(qr.data, location.origin);
          const regID = url.searchParams.get("reg");
          const token = url.searchParams.get("token");
          if (regID) processQr(regID, token || "");
        } catch {}
      }
    }
    rafRef.current = requestAnimationFrame(scanFrame);
  }, [bid]); // eslint-disable-line

  const startCamera = async () => {
    setCamError("");
    // getUserMedia only works on HTTPS (or localhost). Fail loudly instead of a black screen.
    if (!navigator.mediaDevices?.getUserMedia) {
      setCamState("error");
      setCamError(window.isSecureContext
        ? "This browser doesn't support camera access. Use manual search below."
        : "Camera needs a secure (https) connection. Open this page over https, or use manual search below.");
      return;
    }
    setCamState("starting");
    try {
      let stream;
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: { ideal: "environment" }, width: { ideal: 1280 }, height: { ideal: 720 } }
        });
      } catch {
        // Fall back to any available camera (some laptops/desktops have no "environment" cam)
        stream = await navigator.mediaDevices.getUserMedia({ video: true });
      }
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play().catch(() => {});
        setCamState("on");
        if (rafRef.current) cancelAnimationFrame(rafRef.current);
        rafRef.current = requestAnimationFrame(scanFrame);
      }
    } catch (err) {
      setCamState("error");
      const name = err?.name || "";
      setCamError(
        name === "NotAllowedError" ? "Camera permission was blocked. Allow camera access in your browser, then tap Retry."
        : name === "NotFoundError" ? "No camera found on this device. Use manual search below."
        : name === "NotReadableError" ? "Camera is in use by another app. Close it and tap Retry."
        : "Couldn't start the camera. Tap Retry, or use manual search below."
      );
    }
  };

  const stopCamera = () => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    if (videoRef.current?.srcObject) {
      videoRef.current.srcObject.getTracks().forEach(t => t.stop());
      videoRef.current.srcObject = null;
    }
    setCamState("idle");
  };

  useEffect(() => {
    if (tab === "scanner" && bid && authed) startCamera();
    else stopCamera();
    return () => stopCamera();
  }, [tab, bid, authed]); // eslint-disable-line

  const manualCheckin = async () => {
    if (!manualQ.trim() || !bid) return;
    scanLock.current = true;
    const d = await api.checkin(bid, manualQ.trim(), "Main", 1).catch(() => null);
    if (d && d.success && d.checkedIn) {
      flashResult("ok", "✓", d.name, "Manual check-in");
      setHistory(h => [{ name: d.name, type: "manual", time: new Date() }, ...h].slice(0, 30));
      setManualQ("");
      loadStats();
    } else if (d && d.success && d.matches) {
      flashResult("warn", "⚠", d.matches[0].name, d.matches[0].alreadyCheckedIn ? "Already checked in" : "Multiple results");
    } else {
      flashResult("fail", "✗", "Not found", (d && d.message) || "No match.");
    }
  };

  const addWalkIn = async () => {
    if (!wiForm.name.trim()) { setWiStatus({ ok: false, msg: "Name is required." }); return; }
    const d = await api.addWalkIn(bid, wiForm.name.trim(), wiForm.email.trim(), wiForm.usn.trim(), "Main", 1).catch(() => null);
    if (d && d.success) {
      setWiStatus({ ok: true, msg: `${wiForm.name} added. Total: ${d.totalCheckins}` });
      setHistory(h => [{ name: wiForm.name, type: "walkin", time: new Date() }, ...h].slice(0, 30));
      setWiForm({ name: "", email: "", usn: "" });
      loadStats();
    } else {
      setWiStatus({ ok: false, msg: (d && d.message) || "Failed." });
    }
  };

  // OTP login flow (same as checkin.html)
  const [loginStep,   setLoginStep]   = useState("email");
  const [loginEmail,  setLoginEmail]  = useState("");
  const [loginCode,   setLoginCode]   = useState("");
  const [loginMsg,    setLoginMsg]    = useState("");

  const sendOtp = async () => {
    if (!loginEmail.includes("@")) { setLoginMsg("Enter a valid email."); return; }
    const d = await api.requestOtp(loginEmail.trim().toLowerCase()).catch(() => null);
    if (d?.success) { setLoginStep("code"); setLoginMsg(d.message || "Code sent."); }
    else setLoginMsg(d?.message || "Failed.");
  };

  const verifyOtp = async () => {
    const d = await api.verifyOtp(loginEmail.trim().toLowerCase(), loginCode.trim()).catch(() => null);
    if (d?.success && d?.token) {
      localStorage.setItem("authToken", d.token);
      localStorage.setItem("authEmail", d.email);
      localStorage.setItem("authRole",  d.role);
      localStorage.setItem("authHalls", JSON.stringify(d.myHallIds || []));
      setAuthed(true); setEmail(d.email);
    } else setLoginMsg(d?.message || "Invalid code.");
  };

  const logout = () => {
    ["authToken","authEmail","authRole","authHalls"].forEach(k => localStorage.removeItem(k));
    setAuthed(false); setEmail(""); stopCamera();
  };

  const S = {
    body: { fontFamily:"'Inter',system-ui,sans-serif", background:"#0f172a", color:"#f1f5f9", minHeight:"100vh", display:"flex", flexDirection:"column", fontSize:14 },
    topbar: { background:"#1e293b", borderBottom:"1px solid #334155", padding:"0 14px", height:50, display:"flex", alignItems:"center", justifyContent:"space-between", flexShrink:0 },
    statsBar: { background:"#1e293b", padding:"8px 14px", display:"flex", gap:12, borderBottom:"1px solid #334155", flexShrink:0 },
    tabs: { display:"flex", background:"#1e293b", borderBottom:"1px solid #334155", flexShrink:0 },
    tab: (active) => ({ flex:1, padding:"9px 0", border:"none", background:"transparent", color:active?"#3b82f6":"#94a3b8", fontSize:11, fontWeight:500, cursor:"pointer", display:"flex", flexDirection:"column", alignItems:"center", gap:2, fontFamily:"inherit", borderBottom:active?"2px solid #3b82f6":"2px solid transparent" }),
    panels: { flex:1, overflow:"hidden", position:"relative" },
    panel: (active) => ({ position:"absolute", inset:0, overflowY:"auto", display:active?"flex":"none", flexDirection:"column" }),
    // Scanner
    scannerWrap: { flex:1, position:"relative", background:"#000", minHeight:280, overflow:"hidden" },
    video: { width:"100%", height:"100%", objectFit:"cover", position:"absolute", inset:0 },
    overlay: { position:"absolute", inset:0, zIndex:10, display:"flex", alignItems:"center", justifyContent:"center", pointerEvents:"none" },
    resultFlash: (type) => ({
      position:"absolute", inset:0, zIndex:20, display:"flex", alignItems:"center", justifyContent:"center", flexDirection:"column", gap:12, padding:20, textAlign:"center",
      background: type==="ok"?"rgba(16,185,129,.92)":type==="warn"?"rgba(245,158,11,.9)":"rgba(239,68,68,.92)"
    }),
    searchBar: { background:"#1e293b", padding:"10px 14px", display:"flex", gap:8, flexShrink:0 },
    input: { flex:1, background:"#334155", border:"1px solid #334155", borderRadius:8, padding:"8px 10px", fontSize:13, color:"#f1f5f9", fontFamily:"inherit" },
    btn: (color="#3b82f6") => ({ background:color, color:"#fff", border:"none", borderRadius:8, padding:"8px 12px", fontSize:12, fontWeight:500, cursor:"pointer", fontFamily:"inherit", whiteSpace:"nowrap" }),
    card: { background:"#1e293b", margin:14, borderRadius:12, padding:16 },
    label: { fontSize:11, color:"#94a3b8", marginBottom:4, display:"block" },
    inp: { width:"100%", background:"#0f172a", border:"1px solid #334155", borderRadius:8, padding:"9px 10px", fontSize:13, color:"#f1f5f9", fontFamily:"inherit", boxSizing:"border-box", marginBottom:10 },
    histItem: { display:"flex", alignItems:"center", gap:10, padding:"10px 14px", borderBottom:"1px solid #1e293b" },
  };

  if (!authed) return (
    <div style={S.body}>
      <div style={S.topbar}>
        <div style={{ fontWeight:700 }}>BMSIT Check-in</div>
      </div>
      <div style={{ padding:24, maxWidth:360, margin:"0 auto", width:"100%" }}>
        <div style={{ background:"#1e293b", borderRadius:12, padding:20 }}>
          <div style={{ fontWeight:700, marginBottom:16 }}>Sign in to continue</div>
          {loginStep === "email" ? (
            <>
              <label style={S.label}>Email (@bmsit.in)</label>
              <input style={S.inp} type="email" value={loginEmail} onChange={e=>setLoginEmail(e.target.value)} onKeyDown={e=>e.key==="Enter"&&sendOtp()} placeholder="your@bmsit.in" autoFocus />
              <button style={{...S.btn(), width:"100%",padding:"10px"}} onClick={sendOtp}>Send login code</button>
            </>
          ) : (
            <>
              <label style={S.label}>6-digit code sent to {loginEmail}</label>
              <input style={S.inp} type="text" inputMode="numeric" maxLength={6} value={loginCode} onChange={e=>setLoginCode(e.target.value)} onKeyDown={e=>e.key==="Enter"&&verifyOtp()} placeholder="000000" autoFocus />
              <button style={{...S.btn(), width:"100%",padding:"10px"}} onClick={verifyOtp}>Verify & Sign In</button>
              <button style={{background:"transparent",border:"none",color:"#94a3b8",cursor:"pointer",marginTop:8,width:"100%",fontSize:12}} onClick={()=>setLoginStep("email")}>← Back</button>
            </>
          )}
          {loginMsg && <div style={{ marginTop:10, fontSize:12, color:loginMsg.includes("sent")||loginMsg.includes("success")?"#10b981":"#ef4444" }}>{loginMsg}</div>}
        </div>
      </div>
    </div>
  );

  if (promptBid) return (
    <div style={S.body}>
      <div style={S.topbar}>
        <div style={{ fontWeight:700 }}>BMSIT Check-in</div>
        <button style={{...S.btn("#475569"), fontSize:11, padding:"4px 10px"}} onClick={logout}>Sign out</button>
      </div>
      <div style={{ padding:24, maxWidth:360, margin:"0 auto", width:"100%" }}>
        <div style={{ background:"#1e293b", borderRadius:12, padding:20 }}>
          <div style={{ fontWeight:700, marginBottom:4 }}>Select event</div>
          <div style={{ fontSize:12, color:"#94a3b8", marginBottom:16 }}>Enter the Booking ID for the event you're checking in for.</div>
          <label style={S.label}>Booking ID</label>
          <input style={S.inp} value={bidInput} onChange={e=>setBidInput(e.target.value)} onKeyDown={e=>e.key==="Enter"&&bidInput&&(setBid(bidInput),setPromptBid(false))} placeholder="e.g. BSN042" autoFocus />
          <button style={{...S.btn(), width:"100%",padding:10}} onClick={()=>bidInput&&(setBid(bidInput),setPromptBid(false))}>Continue</button>
        </div>
      </div>
    </div>
  );

  return (
    <div style={{ ...S.body, overflow:"hidden", height:"100vh" }}>
      {/* Top bar */}
      <div style={S.topbar}>
        <div>
          <div style={{ fontWeight:700, fontSize:14 }}>Check-in · {bid}</div>
          <div style={{ fontSize:10, color:"#94a3b8" }}>{email}</div>
        </div>
        <div style={{ display:"flex", gap:8 }}>
          <button style={{...S.btn("#334155"), fontSize:11, padding:"4px 10px"}} onClick={()=>setPromptBid(true)}>Change event</button>
          <button style={{...S.btn("#475569"), fontSize:11, padding:"4px 10px"}} onClick={logout}>Sign out</button>
        </div>
      </div>

      {/* Stats bar */}
      <div style={S.statsBar}>
        {[["Checked in",stats.checked],["Total",stats.total],["Walk-ins",stats.walkins],["Rate",stats.rate]].map(([l,v])=>(
          <div key={l} style={{ display:"flex", flexDirection:"column", alignItems:"center", minWidth:56 }}>
            <div style={{ fontSize:18, fontWeight:800 }}>{v}</div>
            <div style={{ fontSize:10, color:"#94a3b8", marginTop:1 }}>{l}</div>
          </div>
        ))}
        <button onClick={loadStats} style={{...S.btn("#334155"), fontSize:10, padding:"4px 8px", marginLeft:"auto"}}>↻ Refresh</button>
      </div>

      {/* Tabs */}
      <div style={S.tabs}>
        {[["scanner","QR Scanner","📷"],["walkin","Walk-in","👤"],["history","History","📋"]].map(([id,label,icon])=>(
          <button key={id} style={S.tab(tab===id)} onClick={()=>setTab(id)}>
            <span style={{ fontSize:15 }}>{icon}</span>
            <span>{label}</span>
          </button>
        ))}
      </div>

      {/* Panels */}
      <div style={S.panels}>
        {/* Scanner panel */}
        <div style={S.panel(tab==="scanner")}>
          <div style={S.scannerWrap}>
            <video ref={videoRef} style={S.video} playsInline muted autoPlay />
            <canvas ref={canvasRef} style={{ display:"none" }} />
            {/* Scan frame overlay */}
            <div style={S.overlay}>
              <div style={{ position:"relative", width:200, height:200 }}>
                <div style={{ position:"absolute", top:0, left:0, width:36, height:36, borderTop:"3px solid #fff", borderLeft:"3px solid #fff", borderRadius:"4px 0 0 0" }} />
                <div style={{ position:"absolute", top:0, right:0, width:36, height:36, borderTop:"3px solid #fff", borderRight:"3px solid #fff", borderRadius:"0 4px 0 0" }} />
                <div style={{ position:"absolute", bottom:0, left:0, width:36, height:36, borderBottom:"3px solid #fff", borderLeft:"3px solid #fff", borderRadius:"0 0 0 4px" }} />
                <div style={{ position:"absolute", bottom:0, right:0, width:36, height:36, borderBottom:"3px solid #fff", borderRight:"3px solid #fff", borderRadius:"0 0 4px 0" }} />
              </div>
            </div>
            {/* Scan hint */}
            <div style={{ position:"absolute", bottom:14, left:0, right:0, textAlign:"center", color:"rgba(255,255,255,.65)", fontSize:11, zIndex:10 }}>
              {qrReady ? "Point at QR code on ticket or ID card barcode" : "Loading scanner…"}
            </div>
            {/* Camera state overlay — replaces the opaque black box when the camera isn't live */}
            {camState !== "on" && !flash && (
              <div style={{ position:"absolute", inset:0, zIndex:15, display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", gap:12, padding:24, textAlign:"center", background:"rgba(15,23,42,.82)" }}>
                <div style={{ fontSize:34 }}>{camState === "error" ? "📷" : "📸"}</div>
                {camState === "starting" ? (
                  <div style={{ color:"#cbd5e1", fontSize:13 }}>Starting camera…</div>
                ) : (
                  <>
                    <div style={{ color:"#e2e8f0", fontSize:13, maxWidth:280, lineHeight:1.4 }}>
                      {camError || "Tap below to start the camera and scan tickets."}
                    </div>
                    <button style={{...S.btn(), padding:"9px 18px"}} onClick={startCamera}>
                      {camState === "error" ? "Retry camera" : "Start camera"}
                    </button>
                  </>
                )}
              </div>
            )}
            {/* Result flash */}
            {flash && (
              <div style={S.resultFlash(flash.type)} onClick={dismissFlash}>
                <div style={{ fontSize:48, color:"#fff" }}>{flash.icon}</div>
                <div style={{ fontSize:18, fontWeight:700, color:"#fff", lineHeight:1.2 }}>{flash.name}</div>
                <div style={{ fontSize:12, color:"rgba(255,255,255,.85)" }}>{flash.sub}</div>
                <button onClick={dismissFlash} style={{ padding:"7px 20px", borderRadius:999, background:"rgba(255,255,255,.25)", border:"1px solid rgba(255,255,255,.4)", color:"#fff", fontSize:12, fontWeight:500, cursor:"pointer", fontFamily:"inherit" }}>Dismiss</button>
              </div>
            )}
          </div>
          {/* Manual search bar */}
          <div style={S.searchBar}>
            <input style={S.input} value={manualQ} onChange={e=>setManualQ(e.target.value)} onKeyDown={e=>e.key==="Enter"&&manualCheckin()} placeholder="Type USN / College ID / Name and press Enter" />
            <button style={S.btn()} onClick={manualCheckin}>Check in</button>
          </div>
        </div>

        {/* Walk-in panel */}
        <div style={S.panel(tab==="walkin")}>
          <div style={S.card}>
            <div style={{ fontWeight:600, marginBottom:16 }}>Add walk-in participant</div>
            <label style={S.label}>Full name *</label>
            <input style={S.inp} value={wiForm.name} onChange={e=>setWiForm(p=>({...p,name:e.target.value}))} placeholder="Full name" autoFocus />
            <label style={S.label}>Email</label>
            <input style={S.inp} type="email" value={wiForm.email} onChange={e=>setWiForm(p=>({...p,email:e.target.value}))} placeholder="name@bmsit.in" />
            <label style={S.label}>USN / College ID</label>
            <input style={S.inp} value={wiForm.usn} onChange={e=>setWiForm(p=>({...p,usn:e.target.value}))} placeholder="1BM22CS001" />
            {wiStatus && (
              <div style={{ padding:"8px 12px", borderRadius:8, marginBottom:10, background:wiStatus.ok?"rgba(16,185,129,.15)":"rgba(239,68,68,.15)", color:wiStatus.ok?"#10b981":"#ef4444", fontSize:13 }}>
                {wiStatus.ok ? "✓ " : "✗ "}{wiStatus.msg}
              </div>
            )}
            <button style={{...S.btn(), width:"100%", padding:12}} onClick={addWalkIn}>Add walk-in</button>
          </div>
        </div>

        {/* History panel */}
        <div style={S.panel(tab==="history")}>
          {history.length === 0 ? (
            <div style={{ textAlign:"center", padding:40, color:"#64748b", fontSize:12 }}>
              <div style={{ fontSize:28, marginBottom:8 }}>📋</div>
              No check-ins yet this session.
            </div>
          ) : history.map((c, i) => (
            <div key={i} style={S.histItem}>
              <div style={{ width:32, height:32, borderRadius:8, background:c.type==="walkin"?"rgba(245,158,11,.15)":"rgba(16,185,129,.15)", display:"flex", alignItems:"center", justifyContent:"center" }}>
                {c.type==="walkin" ? "👤" : "✓"}
              </div>
              <div style={{ flex:1 }}>
                <div style={{ fontWeight:500, fontSize:13 }}>{c.name}</div>
                <div style={{ fontSize:11, color:"#94a3b8" }}>{c.type === "walkin" ? "Walk-in" : c.type === "manual" ? "Manual" : "QR scan"}</div>
              </div>
              <div style={{ fontSize:11, color:"#94a3b8" }}>{c.time.toLocaleTimeString("en-IN",{hour:"2-digit",minute:"2-digit"})}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
